import DotoSocket from './doto-socket'

import qs from 'qs'
import file from './file'
import axios from '../axiosClient'
import { get, cloneDeep } from 'lodash'
import AuthProvider from '../lib/auth/provider'

const _CORE = {
  isApplication: true,
  vueInitialized: false,
  components: [],
  avatarUrl: '/static/avatar.png',
  apiBasePath: '//' + document.location.host,
  assetPath: '/node',
  externalParams: {},
  requestRoute: '',
  requestAction: '',
  requestController: '',
  routedUrl: '',
  notificationPosition: 'top right',
  notificationDuration: 5000,
  sqlParams: {},
  intHost: null,
  extHost: null,
  mobilHost: null,
  creatorHost: null,
  modelLockTimeout: 5000,
  userLockTimeout: 1800001 /* 30 min */,
  hintDictionary: ['app', 'knex', 'Queries'],
  subSystem: {
    id: 26,
    code: 'INT',
  },
  theme: 'default',
  commit: '',
  webSocket: {
    protocol: null,
    host: null,
  },
  params: {},
  intervalTimers: [],
}

/**
 * @description a core objektum eloallitasa
 * @return {Promise<{components: *[], vueInitialized: boolean, subSystem: {code: string, id: number}, notificationPosition: string, apiBasePath: string, commit: string, language: string, requestRoute: string, webSocket: {host: *}, requestController: string, assetPath: string, theme: string, isApplication: boolean, intHost: *, avatarUrl: string, requestAction: string, lockTimeout: number, sqlParams: {}, landingPage: string, params: {ID_S_USER: null}, env: null, routedUrl: string, modelLockTimeout: *, externalParams: {}, extHost: *, hintDictionary: string[], formHost: *, notificationDuration: *}>}
 */
export async function createCore(fullConfig = {}) {
  const CORE = cloneDeep(_CORE)
  const config = get(window, 'config', {})

  let frontendResponse
  try {
    frontendResponse = await axios().get('/api/Layouts/get-layout-content', {
      params: {
        route: get(fullConfig, 'CORE.params.route', document.location.pathname.replace(/^\/*/, '')),
      },
    })
  } catch (error) {
    const status = get(error, 'response.status', 511)

    const authenticator = AuthProvider.getAuthenticator()
    const logoutUrl = authenticator?.getLogoutUrl(document.location.href)

    switch (status) {
      case 404:
        window.location = `/404.html${logoutUrl ? `?logout-url=${encodeURIComponent(logoutUrl)}` : ''}`
        break
      case 403:
        window.location = `/403.html${logoutUrl ? `?logout-url=${encodeURIComponent(logoutUrl)}` : ''}`
        break
      case 503:
        window.location = `/503.html${logoutUrl ? `?logout-url=${encodeURIComponent(logoutUrl)}` : ''}`
        break
      default:
        window.location = `/500.html${logoutUrl ? `?logout-url=${encodeURIComponent(logoutUrl)}` : ''}`
        break
    }
  }

  const route = get(frontendResponse, 'data.url.route', document.location.pathname.slice(1))
  const urlParams = get(frontendResponse, 'data.params', {})

  const customEnvVariables = get(fullConfig, 'frontendConfig', {})
  const frontendConfig = { ...config, ...customEnvVariables }

  const coreParams = get(fullConfig, 'CORE', {})
  Object.assign(CORE.params, get(coreParams, 'params', {}))
  CORE.routedUrl = `${window.location.origin}/${route}?${qs.stringify(CORE.params)}`

  const path = get(coreParams, 'params.route', '/').split('/')

  CORE.language =
    CORE.language ||
    get(CORE.params, 'language', {
      id: 2,
      name: 'Magyar',
      code: 'hu-HU',
    })

  CORE.env = Object.assign(frontendConfig, get(coreParams, 'env', {}))
  const getHost = function (code) {
    const scheme = get(coreParams, `subsystems.${code}.PUBLIC.SCHEME`)
    const host = get(coreParams, `subsystems.${code}.PUBLIC.HOST`)
    const port = get(coreParams, `subsystems.${code}.PUBLIC.PORT`)
    return `${scheme}://${host}${port ? `:${port}` : ''}`
  }
  CORE.subSystem = coreParams.subSystem || CORE.subSystem
  CORE.requestController = path.length > 2 ? `${path[0]}/${path[1]}` : path[0]
  CORE.requestAction = path[path.length - 1]
  CORE.requestRoute = route

  CORE.title = coreParams.title
  CORE.description = coreParams.description
  CORE.keywords = coreParams.keywords

  CORE.apiBasePath = '//' + document.location.host

  Object.assign(CORE.externalParams, urlParams)

  CORE.externalParams = Object.assign({}, coreParams.externalParams, get(frontendResponse, 'data.params', {}))
  CORE.enabledFunctions = get(fullConfig, 'enabledFunctions', [])
  CORE.userShortcuts = get(fullConfig, 'userShortcuts', [])

  Object.assign(CORE.sqlParams, get(coreParams, 'sqlParams'))
  CORE.params.ID_S_USER = get(coreParams, 'externalParams.user')

  CORE.params.userName = get(coreParams, 'externalParams.userName')
  CORE.params.emailNotified = get(coreParams, 'externalParams.emailNotified')

  CORE.url = route
  CORE.layout = get(frontendResponse, 'data.layout', {})
  CORE.landingPage = get(CORE, 'env.landingPage')
  CORE.expressionUrls = get(CORE, 'env.expressionUrls')
  CORE.helpyUrl = get(CORE, 'env.helpyUrl')

  CORE.notificationDuration = get(CORE, 'env.notificationDuration')
  CORE.modelLockEnabled = get(coreParams, 'modelLockEnabled', CORE.modelLockEnabled)
  CORE.modelLockTimeout = get(coreParams, 'modelLockTimeout', CORE.modelLockTimeout)
  CORE.userLockTimeout = get(coreParams, 'userLockTimeout', CORE.userLockTimeout)

  CORE.intHost = getHost('INT')
  CORE.extHost = getHost('EXT')
  CORE.mobilHost = getHost('MOBIL')
  CORE.creatorHost = getHost('CREATOR')

  const simplePayResponse = get(fullConfig, 'CORE.simplePayResponse')
  if (simplePayResponse) CORE.simplePayResponse = simplePayResponse

  const isSocketEnabled = get(CORE.env, 'socket.enabled', false)
  if (isSocketEnabled && CORE.webSocket) {
    CORE.webSocket.host = get(CORE.env, 'socket.host', null)
    const defaultProtocol = CORE.webSocket.host.indexOf('wss') === 0 ? 'wss' : 'ws'
    CORE.webSocket.protocol = get(CORE.env, 'socket.host', defaultProtocol)
    CORE.socket = new DotoSocket(CORE.webSocket.host).connect({ userId: get(CORE, 'externalParams.user', null) })
  }

  CORE.version = get(coreParams, 'version')
  CORE.sentry = get(coreParams, 'sentry', {})

  CORE.getStaticImageSource = get(file, 'methods.getStaticImageSource', () => {})

  return CORE
}
